import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import Sidebar from './Sidebar';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  overflow: auto;
  box-sizing: border-box;
`;

const MainContent = styled.main`
  flex: 1;
  background-color: #F9FAFB;
  min-height: 100vh;
  overflow: auto;
  position: relative;
  box-sizing: border-box;
`;

const ContentArea = styled.div`
  flex: 1;
  margin-top: 64px;
  height: calc(100vh - 64px);
  overflow: auto;
  position: relative;
  box-sizing: border-box;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
  
  /* Firefox scrollbar */
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;

  @media (min-width: 768px) {
    padding: 0;
  }
  
  /* Home page specific styling only */
  &.home-content {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    overflow: hidden; /* Keep overflow hidden only for home page */
    
    /* Hide scrollbar but allow scrolling if needed for home page */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera */
    }
  }
  
  /* Non-home pages get padding and can scroll */
  &:not(.home-content) {
    padding: 0.5rem;
    
    @media (min-width: 768px) {
      padding: 1rem;
    }
  }
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.25);
  z-index: 99;
`;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/home' || location.pathname === '/';

  useEffect(() => {
    // Add a class to the body element for the home page
    if (isHomePage) {
      document.body.classList.add('home-page');
    } else {
      document.body.classList.remove('home-page');
    }
    return () => {
      document.body.classList.remove('home-page');
    };
  }, [isHomePage]);

  const handleSidebarOpen = () => setSidebarOpen(true);
  const handleSidebarClose = () => setSidebarOpen(false);

  return (
    <LayoutContainer>
      <Sidebar open={sidebarOpen} onClose={handleSidebarClose} />
      <MainContent>
        <Navbar onHamburgerClick={handleSidebarOpen} />
        {sidebarOpen && <Backdrop onClick={handleSidebarClose} />}
        <ContentArea className={isHomePage ? 'home-content' : ''}>
          {children}
        </ContentArea>
      </MainContent>
    </LayoutContainer>
  );
};

export default Layout;